import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Space,
  Tag,
  Tooltip,
  message,
  Modal,
  Input,
} from 'antd';
import { DownloadOutlined, CopyOutlined, EditOutlined, CheckCircleOutlined } from '@ant-design/icons';
import mermaid from 'mermaid';
import { Diagram, Project } from '../types';
import bedrockService from '../services/bedrockService';
import { extractErrorMessage } from '../utils/errorHandler';

const { TextArea } = Input;

interface DiagramsViewerProps {
  diagrams?: Diagram[];
  errorMessage?: string | null;
  onGenerateDiagrams: () => void;
  generateDiagramsLoading: boolean;
  onOpenModal: (diagramScript: string, title: string) => void;
  // Design Document props
  designDocumentUrl?: string;
  confluencePageUrl?: string;
  onGenerateDesignDocument: () => void;
  onUploadToConfluence: () => void;
  designDocumentLoading: boolean;
  confluenceLoading: boolean;
  canEdit?: boolean;
  canCreate?: boolean;
  projectId?: string;
  onProjectUpdate?: (project: Project | ((prev: Project | null) => Project)) => void;
  diagramsApproved?: boolean;
  onApprovalChange?: () => void;
  onSyncStages?: (originStage: string, prompt: string, singleEditCallback: () => void | Promise<void>, closeCallback?: () => void) => void;
}

const DiagramsViewer: React.FC<DiagramsViewerProps> = ({
  diagrams,
  errorMessage,
  onGenerateDiagrams,
  generateDiagramsLoading,
  onOpenModal,
  designDocumentUrl,
  confluencePageUrl,
  onGenerateDesignDocument,
  onUploadToConfluence,
  designDocumentLoading,
  confluenceLoading,
  canEdit = false,
  canCreate = false,
  projectId,
  onProjectUpdate,
  diagramsApproved = false,
  onApprovalChange,
  onSyncStages
}) => {
  // Edit diagram state
  const [editDiagramModalVisible, setEditDiagramModalVisible] = useState(false);
  const [editDiagramPrompt, setEditDiagramPrompt] = useState('');
  const [editDiagramLoading, setEditDiagramLoading] = useState(false);
  const [selectedDiagram, setSelectedDiagram] = useState<Diagram | null>(null);

  // Helper function to get diagram display name
  // Converts type enum to readable name if name is missing
  const getDiagramDisplayName = (diagram: Diagram): string => {
    if (diagram.name) return diagram.name;
    // Fallback: convert type enum to readable name
    const typeStr = String(diagram.type);
    switch (typeStr) {
      case 'HIGH_LEVEL_DESIGN':
        return 'High-Level System Design';
      case 'DATA_FLOW':
        return 'Data Flow Diagram';
      case 'LOW_LEVEL_DESIGN':
        return 'Component Diagram';
      case 'API':
        return 'API Schema';
      default:
        return typeStr?.replace(/_/g, ' ') || 'Unknown Diagram';
    }
  };

  // Debug logging - show all diagrams received
  console.log('DiagramsViewer - diagrams:', diagrams);
  console.log('DiagramsViewer - diagrams length:', diagrams?.length);
  console.log('DiagramsViewer - should show generate button:', !diagrams || diagrams.length === 0);
  
  // Detailed diagram logging
  if (diagrams && diagrams.length > 0) {
    console.log('========== ALL DIAGRAMS ==========');
    diagrams.forEach((d, i) => {
      console.log(`Diagram ${i + 1}: name="${d.name}", type="${d.type}", id="${d.id}"`);
    });
    console.log('==================================');
  }

  // Handle edit diagram
  const handleEditDiagram = (diagram: Diagram) => {
    setSelectedDiagram(diagram);
    setEditDiagramPrompt('');
    setEditDiagramModalVisible(true);
  };

  // Handle edit diagram submit
  const handleEditDiagramSubmit = async () => {
    if (!editDiagramPrompt.trim()) {
      message.error('Please enter edit instructions');
      return;
    }

    if (!projectId) {
      message.error('Project ID is missing');
      return;
    }

    if (!selectedDiagram) {
      message.error('No diagram selected');
      return;
    }

    setEditDiagramLoading(true);
    try {
      // Use the helper function to get diagram name
      const diagramName = getDiagramDisplayName(selectedDiagram);
      
      // Comprehensive logging for debugging diagram edit
      console.log('========== DIAGRAM EDIT REQUEST ==========');
      console.log('Project ID:', projectId);
      console.log('Diagram Name (sent to API):', diagramName);
      console.log('Diagram Type (enum):', selectedDiagram.type);
      console.log('Original Name from diagram:', selectedDiagram.name);
      console.log('Edit Prompt:', editDiagramPrompt);
      console.log('Full Diagram Object:', JSON.stringify(selectedDiagram, null, 2));
      console.log('==========================================');

      const response = await bedrockService.editDiagrams(
        projectId,
        editDiagramPrompt,
        diagramName
      );

      if (response && response.status === 'success' && response.data && response.data.diagrams) {
        message.success(`${getDiagramDisplayName(selectedDiagram)} edited successfully!`);
        
        // Update project with new diagrams
        if (onProjectUpdate) {
          onProjectUpdate((prev: Project | null) => {
            if (!prev) return prev!;
            
            // If editing API Schema, clear fullstack-related data
            const updatedArtifacts = {
              ...prev.artifacts,
              diagrams: response.data.diagrams
            };
            
            if (diagramName === 'API Schema') {
              updatedArtifacts.code = {
                ...prev.artifacts.code,
                fullstackRepositoryUrl: undefined,
                testcaseDownloadUrl: undefined,
                isEdited: false,
                editedTimestamp: undefined,
                techStack: undefined
              };
            }
            
            // If editing diagrams used in design document generation, clear design document data
            const diagramsUsedInDesignDoc = ['High-Level System Design', 'Component Diagram', 'Data Flow Diagram'];
            if (diagramsUsedInDesignDoc.includes(diagramName)) {
              updatedArtifacts.designDocumentUrl = undefined;
              updatedArtifacts.designDocumentS3Key = undefined;
            }
            
            return {
              ...prev,
              artifacts: updatedArtifacts
            };
          });
        }

        // Close modal and reset
        setEditDiagramModalVisible(false);
        setEditDiagramPrompt('');
        setSelectedDiagram(null);
      } else {
        const errorMsg = response?.message || response?.data?.message || 'Failed to edit diagram. No valid response received.';
        message.error(errorMsg);
      }
    } catch (error: any) {
      console.error('Error editing diagram:', error);
      const errorMessage = extractErrorMessage(error);
      message.error(errorMessage);
    } finally {
      setEditDiagramLoading(false);
    }
  };
  
  const handleCopyDiagram = async (diagramContent: string, diagramName: string) => {
    try {
      await navigator.clipboard.writeText(diagramContent);
      message.success(`${diagramName} copied to clipboard!`);
    } catch (error) {
      message.error('Failed to copy diagram content');
    }
  };

  const handleDownloadDiagram = async (diagramContent: string, diagramName: string) => {
    try {
      if (diagramName === 'API Schema') {
        // Download as text file for API Schema
        const element = document.createElement('a');
        const file = new Blob([diagramContent], { type: 'text/plain' });
        element.href = URL.createObjectURL(file);
        element.download = `${diagramName.replace(/\s+/g, '_')}.txt`;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);
        message.success(`${diagramName} downloaded successfully!`);
      } else {
        // For Mermaid diagrams, render directly and export as SVG
        try {
          // Initialize mermaid
          mermaid.initialize({ 
            startOnLoad: false,
            theme: 'default',
            securityLevel: 'loose'
          });

          // Generate unique ID for this diagram using crypto API
          const randomBytes = new Uint8Array(8);
          crypto.getRandomValues(randomBytes);
          const diagramId = `diagram-${Date.now()}-${Array.from(randomBytes, b => b.toString(16).padStart(2, '0')).join('')}`;
          
          // Use mermaid.render to get SVG string directly
          const { svg } = await mermaid.render(diagramId, diagramContent);
          
          // Create and download SVG file
          const blob = new Blob([svg], { type: 'image/svg+xml' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${diagramName.replace(/\s+/g, '_')}.svg`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          
          message.success(`${diagramName} downloaded successfully!`);
        } catch (mermaidError) {
          console.error('Mermaid rendering failed:', mermaidError);
          // Fallback to text file if mermaid rendering fails
          const element = document.createElement('a');
          const file = new Blob([diagramContent], { type: 'text/plain' });
          element.href = URL.createObjectURL(file);
          element.download = `${diagramName.replace(/\s+/g, '_')}.txt`;
          document.body.appendChild(element);
          element.click();
          document.body.removeChild(element);
          message.success(`${diagramName} downloaded as text file (SVG rendering failed)`);
        }
      }
    } catch (error) {
      console.error('Download failed:', error);
      message.error('Failed to download diagram');
    }
  };

  // Check if High-Level System Design diagram exists (check both name and type)
  const hasHighLevelDesign = diagrams?.some(d => 
    d.name === 'High-Level System Design' || d.type === 'HIGH_LEVEL_DESIGN'
  );

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px' 
      }}>
        <h3 style={{ margin: 0, color: '#111827', fontSize: '18px', fontWeight: 600 }}>
          Diagrams & Designs {diagrams && diagrams.length > 0 && <span style={{ fontWeight: 400, fontSize: '14px', color: '#6b7280' }}>({diagrams.length} diagram{diagrams.length !== 1 ? 's' : ''})</span>}
        </h3>
        {diagrams && diagrams.length > 0 && (
          <Space>
            {canEdit && !diagramsApproved && (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={onApprovalChange}
                style={{
                  backgroundColor: '#28a745',
                  borderColor: '#28a745',
                  color: '#ffffff'
                }}
              >
                Approve
              </Button>
            )}
            {diagramsApproved && (
              <Button
                type="default"
                icon={<CheckCircleOutlined />}
                disabled
                style={{
                  borderColor: '#28a745',
                  color: '#28a745'
                }}
              >
                Approved
              </Button>
            )}
          </Space>
        )}
      </div>
      
      {errorMessage && (
        <Alert message={errorMessage} type="error" showIcon style={{ marginBottom: 16 }} />
      )}
      
      {/* Design Document Section - Moved to Top */}
      {diagrams && diagrams.length > 0 && hasHighLevelDesign && (
        <Card
          style={{
            marginBottom: '16px',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            backgroundColor: '#f9fafb'
          }}
        >
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: designDocumentUrl ? '0' : '12px'
          }}>
            <h4 style={{
              margin: 0,
              fontSize: '16px',
              fontWeight: 600,
              color: '#111827'
            }}>
              📄 Design Document
            </h4>
            {!designDocumentUrl ? (
              <Tooltip 
                title={!diagrams || diagrams.length === 0 ? 'Please generate diagrams first' : ''}
              >
                {canCreate && (
                  <Button
                    type="primary"
                    onClick={onGenerateDesignDocument}
                    loading={designDocumentLoading}
                    disabled={!diagrams || diagrams.length === 0}
                    style={{
                      backgroundColor: '#1890ff',
                      borderColor: '#1890ff'
                    }}
                  >
                    Generate Design Document
                  </Button>
                )}
              </Tooltip>
            ) : (
              <Space size="middle" wrap>
                <Button
                  type="default"
                  href={designDocumentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  icon={<DownloadOutlined />}
                >
                  View Design Document
                </Button>
                
                {!confluencePageUrl ? (
                  canEdit && (
                    <Button
                      type="primary"
                      onClick={onUploadToConfluence}
                      loading={confluenceLoading}
                      style={{
                        backgroundColor: '#52c41a',
                        borderColor: '#52c41a'
                      }}
                    >
                      Upload to Confluence
                    </Button>
                  )
                ) : (
                  <Button
                    type="primary"
                    href={confluencePageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      backgroundColor: '#52c41a',
                      borderColor: '#52c41a'
                    }}
                  >
                    View Confluence Page
                  </Button>
                )}
              </Space>
            )}
          </div>
          
          {!designDocumentUrl && (
            <div style={{ marginTop: '0', color: '#6b7280', fontSize: '13px' }}>
              ℹ️ Generate a comprehensive technical design document based on your diagrams and requirements.
            </div>
          )}
        </Card>
      )}
      
      {diagrams && diagrams.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {diagrams.map((diagram, index) => (
            <Card
              key={`${diagram.id}-${index}`}
              hoverable
              style={{ 
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.12)';
                e.currentTarget.style.borderColor = '#dc3545';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.borderColor = '#e5e7eb';
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 24px'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '8px'
                  }}>
                    <h4 style={{
                      margin: 0,
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#111827'
                    }}>
                      {getDiagramDisplayName(diagram)}
                    </h4>
                    {/* <Tag style={{ 
                      backgroundColor: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      color: '#374151',
                      borderRadius: '4px'
                    }}>
                      {(diagram?.type || 'HIGH_LEVEL_DESIGN').replace(/_/g, ' ')}
                    </Tag> */}
                  </div>
                  {diagram.requirement_mapping && diagram.requirement_mapping.length > 0 && (
                    <div style={{ marginBottom: '8px' }}>
                      <div style={{ 
                        fontSize: '12px', 
                        color: '#6b7280', 
                        marginBottom: '4px' 
                      }}>
                        Requirements Addressed:
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {diagram.requirement_mapping.map((req: string, i: number) => (
                          <Tag key={i} color="blue" style={{ fontSize: '11px', margin: 0 }}>
                            {req}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <Button
                      type="link"
                      onClick={() => onOpenModal(diagram?.url || diagram?.body || '', getDiagramDisplayName(diagram))}
                      style={{
                        padding: 0,
                        height: 'auto',
                        color: '#0d6aff',
                        fontSize: '14px'
                      }}
                    >
                      {(diagram.name === 'API Schema' || diagram.type === 'API') ? 'Click to View Schema' : 'Click to View Diagram'}
                    </Button>
                    {diagram.lastModified && (
                      <span style={{ 
                        fontSize: '11px', 
                        color: '#6b7280',
                        fontStyle: 'italic',
                        marginLeft: '8px'
                      }}>
                        Last edited: {new Date(diagram.lastModified).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'center'
                }}>
                  {canEdit && (
                    <Tooltip title={(diagram.name === 'API Schema' || diagram.type === 'API') ? "Edit schema" : "Edit diagram"}>
                      <Button
                        icon={<EditOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditDiagram(diagram);
                        }}
                        style={{
                          borderColor: '#dc3545',
                          color: '#dc3545'
                        }}
                      >
                        Edit
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip title={(diagram.name === 'API Schema' || diagram.type === 'API') ? "Copy schema content" : "Copy diagram content"}>
                    <Button
                      icon={<CopyOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyDiagram(diagram?.url || diagram?.body || '', getDiagramDisplayName(diagram));
                      }}
                      style={{
                        borderColor: '#d1d5db',
                        color: '#374151'
                      }}
                    >
                      Copy
                    </Button>
                  </Tooltip>
                  <Tooltip title={(diagram.name === 'API Schema' || diagram.type === 'API') ? "Download schema" : "Download diagram"}>
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadDiagram(diagram?.url || diagram?.body || '', getDiagramDisplayName(diagram));
                      }}
                      style={{
                        borderColor: '#d1d5db',
                        color: '#374151'
                      }}
                    >
                      Download
                    </Button>
                  </Tooltip>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '200px',
          padding: '40px',
          textAlign: 'center'
        }}>
          <Space direction="vertical" size={16} style={{ alignItems: 'center' }}>
            <Alert 
              message="No Diagrams created yet" 
              type="info" 
              showIcon 
              style={{ 
                marginBottom: 0,
                textAlign: 'center'
              }} 
            />
            <Button
              type="primary"
              onClick={onGenerateDiagrams}
              loading={generateDiagramsLoading}
              size="large"
              style={{
                backgroundColor: '#dc3545',
                borderColor: '#dc3545'
              }}
            >
              Generate Diagrams
            </Button>
          </Space>
        </div>
      )}
      
      {/* Edit Diagram Modal */}
      <Modal
        title={`Edit ${selectedDiagram?.name || 'Diagram'}`}
        open={editDiagramModalVisible}
        onOk={() => {
          if (onSyncStages && editDiagramPrompt.trim()) {
            const originDiagramStage = selectedDiagram?.name ? `diagram:${selectedDiagram.name}` : 'diagrams';
            onSyncStages(originDiagramStage, editDiagramPrompt.trim(), () => {
              return handleEditDiagramSubmit();
            }, () => {
              setEditDiagramModalVisible(false);
              setEditDiagramPrompt('');
              setSelectedDiagram(null);
            });
          } else {
            handleEditDiagramSubmit();
          }
        }}
        onCancel={() => {
          setEditDiagramModalVisible(false);
          setEditDiagramPrompt('');
          setSelectedDiagram(null);
        }}
        okText="Apply Changes"
        cancelText="Cancel"
        confirmLoading={editDiagramLoading}
        width={600}
        okButtonProps={{
          disabled: !editDiagramPrompt.trim()
        }}
      >
        <div style={{ marginBottom: '16px' }}>
          <p style={{ marginBottom: '8px', color: '#6b7280' }}>
            Describe the changes you want to make to the {selectedDiagram?.name?.toLowerCase()}:
          </p>
          <TextArea
            value={editDiagramPrompt}
            onChange={(e) => setEditDiagramPrompt(e.target.value)}
            placeholder="e.g., Add a new service for user authentication, Update the API endpoints to include versioning, etc."
            rows={6}
            disabled={editDiagramLoading}
          />
        </div>
        {selectedDiagram?.name === 'API Schema' && (
          <Alert
            message="Important"
            description="Editing the API Schema will delete the existing fullstack code. You will need to regenerate the fullstack code after this operation."
            type="warning"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        )}
        {(selectedDiagram?.name === 'High-Level System Design' || 
          selectedDiagram?.name === 'Component Diagram' || 
          selectedDiagram?.name === 'Data Flow Diagram') && (
          <Alert
            message="Important"
            description="Editing this diagram will delete the existing design document. You will need to regenerate the design document after this operation."
            type="warning"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        )}
        <Alert
          message="Note"
          description="The AI will modify the diagram based on your instructions while maintaining consistency with the existing structure."
          type="info"
          showIcon
        />
      </Modal>
    </div>
  );
};

export default DiagramsViewer;